import { useEffect, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import {
  IconVisa,
  IconMasterCard,
  formatBalance,
  IconArrowRight,
  IconSelected,
  IconClose,
  IconFrozen,
  ClickOutsideDetector,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  InputText,
  Popup,
  ButtonPseudo,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { changeStatus } from '../../hoc/cards/store/slices/item';
import { CardStatus } from '../../hoc/cards/types';
import { CardsRoutes } from '../../routes';
import { CardItemTypes } from './types';

import cn from './card.module.css';

const getStatusIcon = (status: CardStatus) => {
  let icon;

  switch (status) {
    case CardStatus.ORDERED:
      icon = <IconArrowRight />;
      break;
    case CardStatus.ACTIVE:
      icon = <IconSelected />;
      break;
    case CardStatus.CANCELED:
      icon = <IconClose />;
      break;
    case CardStatus.FROZEN:
      icon = <IconFrozen />;
      break;
    default:
      icon = <div />;
      break;
  }

  return icon;
};

const getCardStatusClassName = (status: CardStatus) => {
  let className;

  switch (status) {
    case CardStatus.NEW:
      className = cn.new;
      break;
    case CardStatus.ORDERED:
      className = cn.ordered;
      break;
    case CardStatus.ACTIVE:
      className = cn.active;
      break;
    case CardStatus.FROZEN:
      className = cn.frozen;
      break;
    case CardStatus.CANCELED:
      className = cn.canceled;
      break;
    case CardStatus.VOID:
      className = cn.void;
      break;
    default:
      break;
  }

  return className;
};

const CardItem = ({
  card,
  changeStatusInProgress,
  zIndex,
  setOrderCardPanelVisible,
  cardId,
  id,
  search,
  changeStatusSuccess,
}: CardItemTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [limitsPanelVisible, setLimitPanelVisible] = useState(false);
  const [confirmPopupVisible, setConfirmPopupVisible] = useState(false);
  const [statusForChange, setStatusForChange] = useState<CardStatus>();
  const [limits, setLimits] = useState({
    perMonth: '',
    perWeek: '',
    indefinitely: '',
  });

  const dispatch = useAppDispatch();

  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const navigate = useNavigate();

  const disabledClassName =
    card.status === CardStatus.FROZEN ||
    card.status === CardStatus.CANCELED ||
    card.status === CardStatus.VOID;

  const expDate = new Date(card.expairedDate as string);

  let icon = <div />;
  let paymentType = 'UnionPay';

  if (card.paymentSystem === 'Visa') {
    icon = (
      <IconVisa
        width={40}
        height={13}
        className={`${cn.cardIcon} ${cn.cardIconVisa}`}
      />
    );

    paymentType = 'VISA';
  } else if (card.paymentSystem === 'MC') {
    icon = (
      <IconMasterCard
        width={32}
        height={19}
        className={`${cn.cardIcon} ${cn.cardIconMastercard}`}
      />
    );

    paymentType = 'MC';
  }

  useEffect(() => {
    if (changeStatusSuccess) {
      setConfirmPopupVisible(false);
    }
  }, [changeStatusSuccess]);

  return (
    <>
      <div className={cn.cardItem} style={{ zIndex }}>
        <div
          className={`${cn.cardTop} ${
            disabledClassName ? cn.cardTopDisabled : ''
          }`}
        >
          <div className={cn.cardTopLeft}>
            {icon}
            <div className={cn.cardType}>{`${paymentType} ${
              card.type === 'corporate' ? 'Corp' : 'Debit'
            }`}</div>
            <div className={cn.virtual}>
              {card.virtual ? 'Virtual' : 'Physical'}
            </div>
            <div className={cn.cardNumber}>{card.pan}</div>
            <div className={cn.cardDate}>
              {`${expDate
                ?.getMonth()
                .toString()
                .padStart(2, '0')}/${expDate.getFullYear()}`}
            </div>
          </div>
          <div className={cn.cardTopRight}>
            <div className={cn.cardBalance}>
              {card.balance
                ? `${formatBalance(
                    Number(card.balance),
                  )} ${card?.currency?.toUpperCase()}`
                : '-'}
            </div>
            <div
              className={`${cn.cardStatus} ${getCardStatusClassName(
                card.status,
              )}`}
            >
              {card.status}
            </div>

            <ClickOutsideDetector
              setOptionsListVisible={setMenuVisible}
              className={cn.cardMenuWrapper}
            >
              <>
                <button
                  type="button"
                  className={cn.cardMenuWrapperButton}
                  onClick={() => setMenuVisible(!menuVisible)}
                  aria-label="card menu"
                >
                  <span />
                </button>
                {menuVisible && (
                  <ul className={cn.cardMenu}>
                    <li className={cn.cardMenuItem}>
                      <button
                        type="button"
                        className={cn.cardMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          setLimitPanelVisible(true);
                        }}
                      >
                        <span className={cn.statusName}>Limits</span>
                      </button>
                    </li>
                    <li className={cn.cardMenuItem}>
                      <button
                        type="button"
                        className={cn.cardMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          setOrderCardPanelVisible();
                        }}
                      >
                        <span className={cn.statusName}>Reissue</span>
                      </button>
                    </li>
                    {card.status === CardStatus.ORDERED && (
                      <li className={cn.cardMenuItem}>
                        <button
                          type="button"
                          className={cn.cardMenuButton}
                          onClick={() => {
                            setMenuVisible(false);
                            navigate(
                              generatePath(
                                `${CardsRoutes.CARD_EDIT}${search || ''}`,
                                {
                                  cardId: cardId as string,
                                  id: id as string,
                                  cardEditId: card.id,
                                },
                              ),
                            );
                          }}
                        >
                          <span className={cn.statusName}>Edit</span>
                        </button>
                      </li>
                    )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.ACTIVE,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_ACTIVATE_ACTION,
                      ) && (
                        <li className={cn.cardMenuItem}>
                          <button
                            type="button"
                            className={cn.cardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.ACTIVE);
                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.ACTIVE)}
                            </span>
                            <span className={cn.statusName}>Activate card</span>
                          </button>
                        </li>
                      )}

                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.APPROVED,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_ACCEPT_ACTION,
                      ) && (
                        <li className={cn.cardMenuItem}>
                          <button
                            type="button"
                            className={cn.cardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.APPROVED);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.APPROVED)}
                            </span>
                            <span className={cn.statusName}>Approve card</span>
                          </button>
                        </li>
                      )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.ORDERED,
                    ) && (
                      <li className={cn.cardMenuItem}>
                        <button
                          type="button"
                          className={cn.cardMenuButton}
                          onClick={() => {
                            setConfirmPopupVisible(true);
                            setStatusForChange(CardStatus.ORDERED);

                            setMenuVisible(false);
                          }}
                        >
                          <span className={cn.iconWrapper}>
                            {getStatusIcon(CardStatus.ORDERED)}
                          </span>
                          <span className={cn.statusName}>Order card</span>
                        </button>
                      </li>
                    )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.FROZEN,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_FREEZE_ACTION,
                      ) && (
                        <li className={cn.cardMenuItem}>
                          <button
                            type="button"
                            className={cn.cardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.FROZEN);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.FROZEN)}
                            </span>
                            <span className={cn.statusName}>Freez card</span>
                          </button>
                        </li>
                      )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.CANCELED,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_CANCEL_ACTION,
                      ) && (
                        <li className={cn.cardMenuItem}>
                          <button
                            type="button"
                            className={cn.cardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.CANCELED);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.CANCELED)}
                            </span>
                            <span className={cn.statusName}>Cancel card</span>
                          </button>
                        </li>
                      )}
                  </ul>
                )}
              </>
            </ClickOutsideDetector>
          </div>
        </div>
        <div className={cn.cardBottom}>
          <div className={cn.cardBottomLeft}>
            <div className={cn.cardBottomItem}>{card.account}</div>
            <div className={cn.cardBottomItem}>{card.customer}</div>
          </div>
          <div>
            <div className={cn.cardAddress}>{card.address}</div>
          </div>
        </div>
      </div>
      <Popup
        title={`Change status to ${statusForChange}`}
        onCancelClick={() => setConfirmPopupVisible(false)}
        onOkClick={() =>
          dispatch(
            changeStatus({
              cardId: card.id,
              status: statusForChange as CardStatus,
            }),
          )
        }
        cancelButtonText="Cancel"
        okButtonText={changeStatusInProgress ? 'Loading...' : 'Change'}
        isVisible={confirmPopupVisible}
      />
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={limitsPanelVisible}
        onClose={() => setLimitPanelVisible(false)}
        title="Limits"
        sidepanelExpandedKey="cards-limit-panel"
        footer={
          <>
            <ButtonPrimary
              text="Save"
              onClick={() => setLimitPanelVisible(false)}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => setLimitPanelVisible(false)}
              bold
            />
          </>
        }
      >
        <div className={cn.limitsWrapper}>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="perMonth"
              placeholder="Per month limits"
              onChange={(e) =>
                setLimits({ ...limits, perMonth: e.target.value })
              }
              value={limits.perMonth}
              title="Per Month"
            />
          </div>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="perWeek"
              placeholder="Per week limits"
              onChange={(e) =>
                setLimits({ ...limits, perWeek: e.target.value })
              }
              value={limits.perWeek}
              title="Per Week"
            />
          </div>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="indefinitely"
              placeholder="Indefinitely limits"
              onChange={(e) =>
                setLimits({ ...limits, indefinitely: e.target.value })
              }
              value={limits.indefinitely}
              title="Indefinitely"
            />
          </div>
        </div>
      </SidePanel>
    </>
  );
};

export default CardItem;
